/**
 * Centralized Formatting Utilities (Frontend)
 *
 * Single source of truth for display formatting across all pages.
 * See also: backend/src/utils/formatters.ts for server-side equivalents.
 *
 * @see Spec 042: Formatting Utilities Consolidation
 */

/**
 * Format a number with locale-aware thousand separators.
 *
 * Handles null, undefined, and NaN gracefully.
 *
 * @example
 * formatNumber(1234)      // "1,234"
 * formatNumber(0)         // "0"
 * formatNumber(null)      // "0"
 * formatNumber(undefined) // "0"
 */
export function formatNumber(num: number | null | undefined): string {
  if (num === null || num === undefined || isNaN(num)) return '0';
  return num.toLocaleString();
}

/**
 * Format a number in abbreviated form (K, M).
 *
 * Useful for compact display of large numbers in cards and charts.
 *
 * @example
 * formatNumberCompact(999)       // "999"
 * formatNumberCompact(1500)      // "1.5K"
 * formatNumberCompact(2500000)   // "2.5M"
 */
export function formatNumberCompact(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
}

/**
 * Format bytes into human-readable string
 *
 * Handles null, undefined, NaN, and zero gracefully.
 *
 * @example
 * formatBytes(0)           // "0 B"
 * formatBytes(1536)        // "1.5 KB"
 * formatBytes(1048576)     // "1 MB"
 * formatBytes(null)        // "0 B"
 * formatBytes(undefined)   // "0 B"
 */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || isNaN(bytes) || bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  if (i < 0 || i >= sizes.length) return '0 B';

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}
